import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';

const EnhancedLoadingScreen = ({ isLoading, themeMode, progress = 0 }) => {
  // Add a slight delay before fully hiding to ensure smooth transition
  const [isVisible, setIsVisible] = useState(isLoading);
  const controls = useAnimation();
  
  useEffect(() => {
    if (isLoading) {
      setIsVisible(true);
      controls.start({
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: 'easeOut' }
      });
    } else {
      // Animate out before removing from DOM
      controls.start({
        opacity: 0,
        scale: 0.98,
        transition: { duration: 0.5, ease: 'easeInOut' }
      });
      // Small delay before fully removing from DOM for exit animation
      const timer = setTimeout(() => setIsVisible(false), 650);
      return () => clearTimeout(timer);
    }
  }, [isLoading, controls]);
  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            transition: { duration: 0.5, ease: "easeInOut" }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ 
            backdropFilter: 'blur(10px)', 
            pointerEvents: isLoading ? 'auto' : 'none' 
          }}
        >
          <div className={`absolute inset-0 ${
            themeMode === 'dark' ? 'bg-gray-900/90' : 
            themeMode === 'light' ? 'bg-gray-100/90' : 
            'bg-amber-50/90'
          }`}>
            {/* Decorative elements for vintage theme - Art Deco inspired pattern */}
            {themeMode === 'vintage' && (
              <div className="opacity-10 absolute inset-0 overflow-hidden">
                {/* Top left corner decoration */}
                <div className="absolute top-0 left-0 w-64 h-64">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <path d="M0,0 L100,0 L100,10 L10,10 L10,100 L0,100 Z" fill="currentColor" />
                    <path d="M0,20 L80,20 L80,30 L0,30 Z" fill="currentColor" />
                    <path d="M20,0 L30,0 L30,80 L20,80 Z" fill="currentColor" />
                  </svg>
                </div>
                {/* Bottom right corner decoration */}
                <div className="absolute bottom-0 right-0 w-64 h-64">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <path d="M100,100 L0,100 L0,90 L90,90 L90,0 L100,0 Z" fill="currentColor" />
                    <path d="M100,80 L20,80 L20,70 L100,70 Z" fill="currentColor" />
                    <path d="M80,100 L70,100 L70,20 L80,20 Z" fill="currentColor" />
                  </svg>
                </div>
              </div>
            )}
          </div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={controls}
            className={`relative z-10 p-8 rounded-2xl max-w-lg mx-auto ${
              themeMode === 'dark' ? 'bg-gray-800/90 text-white' : 
              themeMode === 'light' ? 'bg-white/95 text-gray-800' : 
              'bg-amber-100/95 text-amber-900'
            } shadow-2xl ${
              themeMode === 'vintage' ? 'border-2 border-amber-800/30' : ''
            }`}
          >
            <div className="flex flex-col items-center">
              {/* Enhanced Logo animation */}
              <div className="relative w-28 h-28 mb-6">
                <motion.div
                  animate={{ 
                    rotate: 360,
                    transition: { duration: 12, repeat: Infinity, ease: "linear" }
                  }}
                  className="absolute inset-0"
                >
                  <svg viewBox="0 0 100 100" className="w-full h-full opacity-20">
                    <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="2" fill="none" />
                    <circle cx="50" cy="50" r="25" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    {themeMode === 'vintage' && (
                      <>
                        <path d="M50,10 L50,20" stroke="currentColor" strokeWidth="1" />
                        <path d="M50,80 L50,90" stroke="currentColor" strokeWidth="1" />
                        <path d="M10,50 L20,50" stroke="currentColor" strokeWidth="1" />
                        <path d="M80,50 L90,50" stroke="currentColor" strokeWidth="1" />
                        <path d="M24,24 L31,31" stroke="currentColor" strokeWidth="1" />
                        <path d="M69,69 L76,76" stroke="currentColor" strokeWidth="1" />
                        <path d="M24,76 L31,69" stroke="currentColor" strokeWidth="1" />
                        <path d="M69,31 L76,24" stroke="currentColor" strokeWidth="1" />
                      </>
                    )}
                  </svg>
                </motion.div>
                
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center"
                  animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.9, 1, 0.9],
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                >
                  {themeMode === 'vintage' ? (
                    <svg className="w-14 h-14" viewBox="0 0 24 24" fill="none">
                      <path d="M12,3 L20,7 L20,17 L12,21 L4,17 L4,7 L12,3 Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <path d="M12,7 L16,9 L16,15 L12,17 L8,15 L8,9 L12,7 Z" stroke="currentColor" strokeWidth="1" fill="none" />
                      <path d="M12,3 L12,7" stroke="currentColor" strokeWidth="1" />
                      <path d="M20,7 L16,9" stroke="currentColor" strokeWidth="1" />
                      <path d="M20,17 L16,15" stroke="currentColor" strokeWidth="1" />
                      <path d="M12,21 L12,17" stroke="currentColor" strokeWidth="1" />
                      <path d="M4,17 L8,15" stroke="currentColor" strokeWidth="1" />
                      <path d="M4,7 L8,9" stroke="currentColor" strokeWidth="1" />
                    </svg>
                  ) : (
                    <svg className="w-14 h-14" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  )}
                </motion.div>
              </div>
              
              <h2 className={`text-xl md:text-2xl font-bold mb-2 tracking-tight ${
                themeMode === 'vintage' ? 'font-serif uppercase tracking-wider' : 'font-sans'
              }`}>
                {themeMode === 'vintage' ? (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    CITY ARCHIVES LOADING
                  </motion.span>
                ) : (
                  'Loading Planning Data'
                )}
              </h2>
              
              <p className={`text-sm md:text-base mb-6 text-center max-w-sm ${
                themeMode === 'dark' ? 'text-gray-300' : 
                themeMode === 'light' ? 'text-gray-600' : 
                'text-amber-800'
              }`}>
                {themeMode === 'vintage' ? (
                  <>
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.8 }}
                      className="block font-serif"
                    >
                      ACCESSING CITY PLANNING INTELLIGENCE RECORDS...
                    </motion.span>
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.2, duration: 0.8 }}
                      className="block text-xs mt-2 font-mono tracking-wide"
                    >
                      ESTABLISHED 1925 · CITY BUREAU OF DEVELOPMENT
                    </motion.span>
                  </>
                ) : (
                  'Retrieving and processing the latest urban planning applications and data...'
                )}
              </p>
              
              {/* Enhanced Progress bar */}
              <div className={`w-full ${themeMode === 'vintage' ? 'h-1 bg-amber-800/20' : 'h-2 bg-opacity-20 bg-current'} rounded-full overflow-hidden mb-4 ${themeMode === 'vintage' ? 'border border-amber-800/20' : ''}`}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ 
                    width: `${progress}%`,
                    transition: { duration: 0.6, ease: "easeInOut" }
                  }}
                  className={`h-full ${themeMode === 'vintage' ? 'bg-amber-700 relative' : ''}`}
                  style={themeMode === 'vintage' ? undefined : { background: 'var(--c-accent)' }}
                >
                {themeMode === 'vintage' && progress > 5 && (
                  <motion.div 
                    className="absolute inset-y-0 right-0 w-1 bg-amber-100"
                    animate={{
                      opacity: [0.2, 0.8, 0.2],
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                )}
                </motion.div>
              </div>
              
              {/* Percentage text for vintage theme */}
              {themeMode === 'vintage' && (
                <div className="w-full flex justify-between text-xs font-mono text-amber-800/60 mb-4">
                  <span>0%</span>
                  <span className="font-medium">{Math.round(progress)}%</span>
                  <span>100%</span>
                </div>
              )}
              
              {/* Enhanced Animated indicators */}
              {themeMode === 'vintage' ? (
                <div className="flex items-center mt-3 justify-center">
                  <div className="relative w-12 h-6">
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      animate={{
                        opacity: [1, 0.3, 1],
                      }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <svg viewBox="0 0 38 12" className="w-full" fill="currentColor" opacity="0.7">
                        <rect x="0" y="4" width="8" height="4" />
                        <rect x="15" y="4" width="8" height="4" />
                        <rect x="30" y="4" width="8" height="4" />
                      </svg>
                    </motion.div>
                  </div>
                </div>
              ) : (
                <div className="flex space-x-2 items-center mt-3">
                  <motion.div 
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    className="w-2.5 h-2.5 rounded-full bg-current"
                  />
                  <motion.div 
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.2, ease: "easeInOut" }}
                    className="w-2.5 h-2.5 rounded-full bg-current"
                  />
                  <motion.div 
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.4, ease: "easeInOut" }}
                    className="w-2.5 h-2.5 rounded-full bg-current"
                  />
                </div>
              )}
              
              {/* Enhanced footer text */}
              <div className="mt-8">
                {themeMode === 'vintage' ? (
                  <div className="text-center border-t border-amber-800/20 pt-3 mt-2">
                    <motion.p 
                      className="text-xs font-serif tracking-widest text-amber-800/80"
                      animate={{
                        opacity: [0.7, 1, 0.7],
                      }}
                      transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                    >
                      POWERED BY CITY-SCOPE INTELLIGENCE
                    </motion.p>
                    <div className="flex justify-center mt-2">
                      <svg viewBox="0 0 100 6" className="w-24 h-3 opacity-40">
                        <path d="M0,3 L100,3" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M10,1 L10,5" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M20,1 L20,5" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M30,1 L30,5" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M40,1 L40,5" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M50,0 L50,6" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M60,1 L60,5" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M70,1 L70,5" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M80,1 L80,5" stroke="currentColor" strokeWidth="0.5" />
                        <path d="M90,1 L90,5" stroke="currentColor" strokeWidth="0.5" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <p className={`text-xs ${
                    themeMode === 'dark' ? 'text-gray-400' : 
                    themeMode === 'light' ? 'text-gray-500' : 
                    'text-amber-700/70'
                  }`}>
                    Powered by City-Scope Data Processing
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnhancedLoadingScreen;
